// bully ignore[directory-kebab-case: component-directory-required requires this directory to literally equal the PascalCase file stem (AgentRowKebab), and no rule enforces PascalCase .tsx filenames — kebab-casing both dir and file instead trips oxlint's unicorn/filename-case; no shape satisfies all three checks at once. follow-up: revisit if bully's directory-kebab-case gains a component-directory exemption.]
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import MoreVert from '@mui/icons-material/MoreVert'
import PauseCircleOutlined from '@mui/icons-material/PauseCircleOutlined'
import PlayCircleOutlined from '@mui/icons-material/PlayCircleOutlined'
import React from 'react'
import type { DropdownMenuEvent, DropdownMenuItem } from 'sutra/components'
import { KebabButton } from 'sutra/components/KebabButton/KebabButton'

import {
  EditIcon,
  ShareIcon,
} from 'features/trend-detection/features/agent-list/components/AgentActionIcons'
import {
  AgentRowKebabMenu,
  MenuItemIcon,
  MenuItemLabel,
} from 'features/trend-detection/features/agent-list/components/AgentRowKebab/styles'

export type AgentRowKebabHandlers = {
  onViewDetails: () => void
  onEdit: () => void
  onToggleActive: () => void
  onShare: () => void
  onDelete: () => void
}

export type AgentRowKebabItemKey =
  | 'view'
  | 'edit'
  | 'toggle-active'
  | 'share'
  | 'delete'

export type AgentRowKebabProps = {
  agentName: string
  isEditor: boolean
  isActive: boolean
  /**
   * Default agents are managed by Yogi and cannot be edited, paused, shared,
   * or deleted by anyone (including editors). Their kebab collapses to just
   * View details so no action here can 403 or produce a no-op.
   */
  isDefault?: boolean
  /**
   * Items surfaced elsewhere in the surrounding UI. The detail page shows
   * Share + Edit as explicit ActionButtons, so it hides them from the kebab.
   */
  hideItems?: AgentRowKebabItemKey[]
  /**
   * Extra vertical gap (in px) between the trigger button's bottom edge and
   * the top of the opened dropdown. Defaults to 4px. The detail page
   * bumps this so the dropdown sits clearly below the Share/Edit meta row
   * instead of feeling attached to it.
   */
  dropdownOffsetY?: number
  handlers: AgentRowKebabHandlers
}

export const AgentRowKebab: React.FC<AgentRowKebabProps> = ({
  agentName,
  isEditor,
  isActive,
  isDefault = false,
  hideItems,
  dropdownOffsetY,
  handlers,
}) => {
  const toggleLabel = isEditor
    ? isActive
      ? 'Pause'
      : 'Resume'
    : isActive
      ? 'Mute'
      : 'Unmute'
  const iconFor = (glyph: React.ReactNode, danger = false): React.ReactNode => (
    <MenuItemIcon $danger={danger}>{glyph}</MenuItemIcon>
  )
  const labelFor = (text: string, danger = false): React.ReactNode => (
    <MenuItemLabel $danger={danger}>{text}</MenuItemLabel>
  )
  const toggleGlyph = isActive ? (
    <PauseCircleOutlined fontSize="small" />
  ) : (
    <PlayCircleOutlined fontSize="small" />
  )

  const defaultItems: DropdownMenuItem[] = [
    {
      key: 'view',
      label: labelFor('View details'),
      icon: iconFor(<InfoOutlined fontSize="small" />),
    },
  ]

  const hidden = new Set<AgentRowKebabItemKey>(hideItems ?? [])
  const allItems: DropdownMenuItem[] = isDefault
    ? defaultItems
    : isEditor
      ? [
          {
            key: 'view',
            label: labelFor('View details'),
            icon: iconFor(<InfoOutlined fontSize="small" />),
          },
          {
            key: 'edit',
            label: labelFor('Edit'),
            icon: iconFor(<EditIcon />),
          },
          {
            key: 'toggle-active',
            label: labelFor(toggleLabel),
            icon: iconFor(toggleGlyph),
          },
          {
            key: 'share',
            label: labelFor('Share'),
            icon: iconFor(<ShareIcon />),
          },
          { type: 'divider' as const },
          {
            key: 'delete',
            label: labelFor('Delete', true),
            icon: iconFor(<DeleteOutlineOutlined fontSize="small" />, true),
            danger: true,
          },
        ]
      : [
          {
            key: 'view',
            label: labelFor('View details'),
            icon: iconFor(<InfoOutlined fontSize="small" />),
          },
          {
            key: 'toggle-active',
            label: labelFor(toggleLabel),
            icon: iconFor(toggleGlyph),
          },
          {
            key: 'share',
            label: labelFor('Share'),
            icon: iconFor(<ShareIcon />),
          },
        ]

  const items: DropdownMenuItem[] = allItems
    .filter(
      (item) =>
        !(
          'key' in item &&
          typeof item.key === 'string' &&
          hidden.has(item.key as AgentRowKebabItemKey)
        ),
    )
    // Drop dividers whose adjacent items were removed (leading/trailing/back-to-back).
    .filter((item, index, arr) => {
      if ('type' in item && item.type === 'divider') {
        const next = arr[index + 1]
        if (!next || (next && 'type' in next && next.type === 'divider'))
          return false
        if (index === arr.length - 1) return false
      }
      return true
    })

  const handleAction = (key: string, event: DropdownMenuEvent): void => {
    event.stopPropagation()
    switch (key) {
      case 'view':
        handlers.onViewDetails()
        break
      case 'edit':
        handlers.onEdit()
        break
      case 'toggle-active':
        handlers.onToggleActive()
        break
      case 'share':
        handlers.onShare()
        break
      case 'delete':
        handlers.onDelete()
        break
    }
  }

  return (
    <AgentRowKebabMenu
      side="bottom"
      align="end"
      sideOffset={dropdownOffsetY}
      items={items}
      onAction={handleAction}
    >
      <KebabButton
        size="lg"
        aria-label={`Actions for ${agentName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <MoreVert fontSize="small" />
      </KebabButton>
    </AgentRowKebabMenu>
  )
}
